import achievementsData from '../data/achievements.json';
import { GameState } from '../core/GameState.js';

/**
 * HTML/CSS panel showing all achievements (unlocked and locked).
 * Accessible from the main menu.
 */
export class AchievementPanel {
  constructor(parentEl) {
    this.parentEl = parentEl;
    this.el = null;
    this.isOpen = false;
    this._create();
  }

  _create() {
    this.el = document.createElement('div');
    this.el.className = 'achievement-panel';
    this.el.innerHTML = `
      <div class="achievement-header">
        <h2>Achievements</h2>
        <div class="achievement-count" id="achievement-count">0 / 0</div>
      </div>
      <div class="achievement-list" id="achievement-list"></div>
      <button class="achievement-close" id="achievement-close">Close</button>
    `;
    this.el.style.display = 'none';
    this.parentEl.appendChild(this.el);

    const style = document.createElement('style');
    style.textContent = `
      .achievement-panel {
        position: absolute; top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0,0,0,0.92);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        z-index: 200; font-family: monospace; color: #ccc;
      }
      .achievement-header { text-align: center; margin-bottom: 8px; }
      .achievement-header h2 { color: #ffcc44; font-size: 14px; margin: 0 0 4px 0; }
      .achievement-count { font-size: 10px; color: #888; }
      .achievement-list {
        max-width: 400px; width: 90%; max-height: 180px; overflow-y: auto;
        display: grid; grid-template-columns: 1fr 1fr; gap: 4px;
      }
      .achievement-item {
        background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
        padding: 4px 6px; border-radius: 2px;
      }
      .achievement-item.unlocked { border-color: rgba(100,255,100,0.3); }
      .achievement-item.locked { opacity: 0.4; }
      .achievement-name { font-size: 9px; font-weight: bold; }
      .achievement-desc { font-size: 8px; color: #888; }
      .achievement-close {
        margin-top: 10px; background: #aa4444; color: #fff; border: none;
        padding: 4px 16px; font-family: monospace; font-size: 11px; cursor: pointer;
        border-radius: 2px;
      }
      .achievement-close:hover { background: #cc5555; }
      .achievement-toast {
        position: absolute; top: 30px; left: 50%; transform: translateX(-50%);
        background: rgba(0,80,0,0.9); border: 1px solid #44ff44; padding: 4px 12px;
        font-family: monospace; font-size: 10px; color: #88ff88; z-index: 300;
        border-radius: 3px; pointer-events: none; transition: opacity 0.5s;
      }
    `;
    this.parentEl.appendChild(style);

    document.getElementById('achievement-close').addEventListener('click', () => this.close());
  }

  open() {
    this.isOpen = true;
    this.el.style.display = 'flex';
    this._refresh();
  }

  close() {
    this.isOpen = false;
    this.el.style.display = 'none';
  }

  _refresh() {
    const unlocked = new Set(GameState.achievements || []);
    const total = Object.keys(achievementsData).length;
    document.getElementById('achievement-count').textContent = `${unlocked.size} / ${total}`;

    const list = document.getElementById('achievement-list');
    list.innerHTML = '';

    // Show unlocked first, then locked
    const sorted = Object.entries(achievementsData).sort(([aId], [bId]) => {
      const aUnlocked = unlocked.has(aId);
      const bUnlocked = unlocked.has(bId);
      if (aUnlocked && !bUnlocked) return -1;
      if (!aUnlocked && bUnlocked) return 1;
      return 0;
    });

    for (const [id, ach] of sorted) {
      const isUnlocked = unlocked.has(id);
      const item = document.createElement('div');
      item.className = `achievement-item ${isUnlocked ? 'unlocked' : 'locked'}`;
      item.innerHTML = `
        <div class="achievement-name" style="color:${isUnlocked ? '#88ff88' : '#666'}">${isUnlocked ? ach.name : '???'}</div>
        <div class="achievement-desc">${ach.description}</div>
      `;
      list.appendChild(item);
    }
  }

  /**
   * Show a toast notification for a newly unlocked achievement.
   * @param {string} name - Achievement name
   */
  showToast(name) {
    const toast = document.createElement('div');
    toast.className = 'achievement-toast';
    toast.textContent = `Achievement: ${name}`;
    this.parentEl.appendChild(toast);

    setTimeout(() => { toast.style.opacity = '0'; }, 2000);
    setTimeout(() => { toast.remove(); }, 2500);
  }

  destroy() {
    if (this.el && this.el.parentElement) {
      this.el.parentElement.removeChild(this.el);
    }
  }
}
